// ============================================================
// ExpressErrorHandling.jsx
//
// A branded, responsive course content page for the
// "Error Handling in Express" lesson.
//
// Author: ProDev Linux Series (Reliability Branch)
// Features: Async try/catch patterns, centralized middleware,
//           status code mapping, and Dashboard navigation.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import BugReportIcon          from "@mui/icons-material/BugReport";
import ErrorOutlineIcon       from "@mui/icons-material/ErrorOutline";
import HealthAndSafetyIcon    from "@mui/icons-material/HealthAndSafety";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import RuleIcon               from "@mui/icons-material/Rule";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressErrorHandling() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Reliability · Module 16"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Express <span>Error Handling</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Robust error handling ensures your API responds gracefully to the unexpected. At ProDev, we focus on trapping bugs, sanitizing error messages, and maintaining high system availability.
                    </Typography>
                </Box>

                {/* 1. ASYNC TRY-CATCH */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Async/Await Patterns" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Async routes require manual trapping. If an error occurs during a database call, you must catch it and send a proper status code.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.get('/users/:id', async (req, res) => {", comment: "" },
                        { cmd: "  try {", comment: "" },
                        { cmd: "    const user = await User.findById(req.params.id);", comment: "" },
                        { cmd: "    if (!user) return res.status(404).json({ message: 'User Missing' });", comment: "" },
                        { cmd: "    res.json(user);", comment: "" },
                        { cmd: "  } catch (err) {", comment: "" },
                        { cmd: "    res.status(500).json({ error: err.message });", comment: "Internal Failure" },
                        { cmd: "  }", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 2. CENTRALIZED MIDDLEWARE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HealthAndSafetyIcon />} label="2. Global Error Handler" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Define a middleware with <strong style={{color: BRAND.white}}>four arguments</strong> at the very end of your app. Express uses the arity to identify it as the designated error handler.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.use((err, req, res, next) => {", comment: "Must have (err, req, res, next)" },
                        { cmd: "  console.error('Critical Error:', err.stack);", comment: "Log internally" },
                        { cmd: "  res.status(err.status || 500).json({", comment: "" },
                        { cmd: "    success: false,", comment: "" },
                        { cmd: "    message: err.message || 'Server Error'", comment: "" },
                        { cmd: "  });", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 3. STATUS CODES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RuleIcon />} label="3. Response Standard" />
                    <Grid container spacing={2}>
                        {[
                            { code: "400", label: "Bad Request", desc: "Client sent malformed data.", color: "#ff9800" },
                            { code: "401", label: "Unauthorized", desc: "Authentication is required or failed.", color: "#f44336" },
                            { code: "404", label: "Not Found", desc: "The requested resource doesn't exist.", color: "#2196f3" },
                            { code: "500", label: "Server Error", desc: "The server encountered a bug.", color: "#9c27b0" },
                        ].map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.code}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, textAlign: 'center' }}>
                                    <Typography sx={{ color: item.color, fontWeight: 900, fontSize: 18 }}>{item.code}</Typography>
                                    <Typography sx={{ color: BRAND.white, fontSize: 13, fontWeight: 700 }}>{item.label}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 11, mt: 0.5 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Trap and manage asynchronous errors using try/catch",
                            "Utilize next(err) to forward failures to a central handler",
                            "Design a global Error-Handling middleware with 4 arguments",
                            "Differentiate between client-side (4xx) and server-side (5xx) errors",
                            "Implement secure logging practices that protect stack traces",
                            "Standardize API error responses for better frontend consumption",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Never send the full <strong>err.stack</strong> to the client in production. It exposes your folder structure and code logic to potential attackers. Only show detailed stack traces when <code style={{ color: BRAND.goldBright }}>NODE_ENV === 'development'</code>.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
